import * as React from 'react';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { ThemeProvider } from '@mui/material';

import usePageScrollHeldWhileOpen from '../../hooks/usePageScrollHeldWhileOpen';
import { customTheme } from '../../libs';

const FIXED_BELOW_MODIFIERS = [{ name: 'flip', enabled: false }]
const SCREEN_EDGE_MARGIN = 8
const FITS_ON_SCREEN_MODIFIERS = [
  { name: 'flip', enabled: true, options: { altAxis: false, padding: SCREEN_EDGE_MARGIN, fallbackPlacements: ['top-start', 'left-start'] } },
  { name: 'preventOverflow', options: { altAxis: true, tether: false, padding: SCREEN_EDGE_MARGIN } },
  // Quill's layouts have no doctype, so bottom-anchored (upward) positions read the whole page height as the screen's
  { name: 'computeStyles', options: { adaptive: false } },
]

export const DateTimePickerInput = ({
  closeFunction,
  defaultText,
  icon,
  initialValue,
  keyForElement,
  timeFormat,
  shouldFormatDateAsNaiveString=true,
  // callers passing false to escape a scroll container must pair it with click-outside
  // handling that exempts the portaled popup (useModalAccessibility does)
  disablePortal=true,
}: {
  closeFunction: (date: Date |string | null) => void;
  defaultText?: string;
  icon?: React.ReactNode;
  initialValue?: string | Date | null;
  keyForElement?: string;
  timeFormat?: string;
  shouldFormatDateAsNaiveString: boolean;
  disablePortal?: boolean;
}) => {
  const initialDate = initialValue ? new Date(initialValue) : null;
  const [selectedDate, setSelectedDate] = React.useState<Date | null>(initialDate);

  const pageScrollHold = usePageScrollHeldWhileOpen();

  function handleOpen() {
    if (!disablePortal) { pageScrollHold.hold(); }
  }

  function handleClose() { pageScrollHold.releaseOnNextInput(); }

  React.useEffect(() => {
    if (!initialValue) { return }

    setSelectedDate(new Date(initialValue))
  }, [initialValue])

  function formatDateAsNaiveString(date: Date | null): string | null {
    if (!date) return null;
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}`;
  }

  function handleAccept(newValue: Date | null) {
    if (shouldFormatDateAsNaiveString) {
      closeFunction(formatDateAsNaiveString(newValue));
    } else {
      closeFunction(newValue)
    }
  }

  function handleBlur() {
    if (shouldFormatDateAsNaiveString) {
      closeFunction(formatDateAsNaiveString(selectedDate));
    } else {
      closeFunction(selectedDate)
    }
  }

  function handleChange(newValue: Date | null) {
    setSelectedDate(newValue);
  }

  return (
    <ThemeProvider theme={customTheme}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <div className="date-picker-container" key={keyForElement}>
          <div className="icon-and-datetime-picker">
            {icon}
            <DateTimePicker
              format={timeFormat || 'MM/d h:mm a'}
              onAccept={handleAccept}
              onChange={handleChange}
              onClose={handleClose}
              onOpen={handleOpen}
              slotProps={{
                popper: {
                  disablePortal,
                  // a portaled calendar can't be scrolled to (the page scroll is held while it's open), so it must find room on screen
                  modifiers: disablePortal ? FIXED_BELOW_MODIFIERS : FITS_ON_SCREEN_MODIFIERS,
                },
                textField: {
                  'aria-label': defaultText || 'Select date & time',
                  inputProps: { onBlur: handleBlur },
                  placeholder: defaultText || 'Select date & time',
                },
              }}
              value={selectedDate}
            />
          </div>
        </div>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

export default DateTimePickerInput;
